import {
  action_plan_impact_type_enum,
  ActionPlanImpactTrendsWithoutTargetPathOutputFragment,
  ActionPlanImpactTrendsWithTargetPathOutputFragment,
  cost_version_status_enum,
  emission_factor_type_enum,
  EnergyValuesCrremOutputFragment,
  language_enum,
  ModificationImpactCrremFragment,
  RecommendedActionImpactOutputFragment,
} from '@predium/client-graphql';
import { COMMON_DATE_FORMATS, ensureDefined, formatDateToLocale } from '@predium/utils';
import isNil from 'lodash/isNil';
import { HYPHEN } from '../../../utils/constants';
import { getScenarioCreatedAt, getScenarioOwner } from './CompareScenarios/compare-scenarios.utils';
import { ScenarioOutput } from './types';

export const getScenarioNames = (scenarios: { name: string; id: number }[] | undefined, scenarioId?: number) => {
  if (!scenarioId || !scenarios) return [];
  const baseScenarios = scenarioId ? scenarios.filter((s) => s.id !== scenarioId) : scenarios;
  return baseScenarios.map((s) => s.name);
};

export const isModificationImpactCrrem = (obj: any): obj is ModificationImpactCrremFragment => {
  if (!obj) return false;
  return obj.analysisType === emission_factor_type_enum.CRREM;
};

export const isRecommendedActionModificationImpactCrrem = (obj: any): obj is RecommendedActionImpactOutputFragment => {
  if (!obj) return false;
  return obj.analysisType === emission_factor_type_enum.CRREM;
};

export const isEnergyValuesCRREM = (obj: any): obj is EnergyValuesCrremOutputFragment => {
  if (isNil(obj)) return false;
  return obj.analysisType === emission_factor_type_enum.CRREM;
};

export const isActionPlanTrendImpactWithTargetPath = (
  obj: any,
): obj is ActionPlanImpactTrendsWithTargetPathOutputFragment => {
  return obj.type === action_plan_impact_type_enum.WITH_TARGET_AND_STRANDING_RANGES;
};

export const isActionPlanTrendImpactWithoutTargetPath = (
  obj: any,
): obj is ActionPlanImpactTrendsWithoutTargetPathOutputFragment => {
  return obj.type === action_plan_impact_type_enum.WITHOUT_TARGET_AND_STRANDING_RANGES;
};

export function getCostVersionDetails(
  costVersion: ScenarioOutput['scenario_versions'][number]['cost_version'],
  language: language_enum,
) {
  if (!costVersion) return { isOnLatestCostVersion: false, formattedCostVersionDate: HYPHEN };
  const isOnLatestCostVersion = costVersion.status === cost_version_status_enum.ROLLED_OUT;
  const formattedCostVersionDate = formatDateToLocale(
    costVersion.valid_from,
    COMMON_DATE_FORMATS.DAY_MONTH_YEAR,
    language,
  );

  return { isOnLatestCostVersion, formattedCostVersionDate };
}

export function getCompareScenarioDetails(scenario: ScenarioOutput, language: language_enum) {
  const activeScenarioVersion = scenario.scenario_versions.find((version) => version.status === 'ACTIVE');
  const costVersion = ensureDefined(activeScenarioVersion?.cost_version);

  const { isOnLatestCostVersion, formattedCostVersionDate } = getCostVersionDetails(
    {
      status: costVersion.status as cost_version_status_enum,
      version: costVersion.version,
      valid_from: costVersion.valid_from,
    },
    language,
  );

  const ownerAndCreatedAt = `${getScenarioOwner(
    scenario.owner.first_name ?? null,
    scenario.owner.last_name ?? null,
  )} ・ ${getScenarioCreatedAt(scenario?.created_at ? scenario.created_at : null, language)}`;

  return {
    isOnLatestCostVersion,
    ownerAndCreatedAt,
    formattedCostVersionDate,
  };
}
